import React, { Suspense, lazy, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InvitationCard from "../components/InvitationCard";
import Reveal from "../components/Reveal";
import SeasonalEffects from "../components/SeasonalEffects";
import useSeason from "../hooks/useSeason";
import { EVENT_TYPES } from "../data/eventTypes";
import {
  COLORWAYS,
  TEMPLATES,
  formatPrice,
  getTemplate,
  swatchStyle,
} from "../data/templates";
import { sampleEvent } from "../data/samples";
import { demoEvent, samplePhoto } from "../data/demo";
import { useCatalog } from "../context/CatalogContext";

// The website previews sit below the fold, so their code is fetched after
// the top of the page has been shown.
const SitePreview = lazy(() => import("../components/SitePreview"));
const TemplateCarousel = lazy(() => import("../components/TemplateCarousel"));

const STUDIO_TEMPLATES = [
  "jardin",
  "terracotta",
  "minuit-dore",
  "boardroom",
  "neon-night",
];
const STEPS = ["choose", "design", "pay", "share"];
const POINTS = ["p1", "p2", "p3"];
const INCLUDED = ["i1", "i2", "i3", "i4", "i5", "i6"];
const FAQ = ["q1", "q2", "q3", "q4", "q5"];

// "Try it" panel: the same card the editor uses, restyled live.
function Studio() {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [templateId, setTemplateId] = useState(STUDIO_TEMPLATES[0]);
  const [colorway, setColorway] = useState("default");
  const [withPhoto, setWithPhoto] = useState(true);
  const template = getTemplate(templateId);

  const event = {
    ...sampleEvent(t, "wedding"),
    title: title.trim() || t("samples.wedding.title"),
    coverUrl: withPhoto ? samplePhoto : "",
    look: { colorway },
  };

  return (
    <div className="studio">
      <Reveal className="studio__controls" variant="left">
        <label className="field">
          <span className="field__label">{t("landing.studio.names")}</span>
          <input
            className="input"
            type="text"
            maxLength={60}
            placeholder={t("samples.wedding.title")}
            value={title}
            onChange={(change) => setTitle(change.target.value)}
          />
        </label>

        <div className="field">
          <span className="field__label">{t("landing.studio.template")}</span>
          <div className="studio__templates">
            {STUDIO_TEMPLATES.map((id) => (
              <button
                key={id}
                type="button"
                className={`chip${templateId === id ? " is-active" : ""}`}
                onClick={() => setTemplateId(id)}
              >
                {getTemplate(id).name}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="field__label">{t("landing.studio.colour")}</span>
          <div className="swatches">
            {COLORWAYS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`swatch${colorway === option.id ? " is-active" : ""}`}
                style={swatchStyle(template, option.id)}
                aria-label={t(`editor.story.colorways.${option.id}`)}
                aria-pressed={colorway === option.id}
                onClick={() => setColorway(option.id)}
              />
            ))}
          </div>
        </div>

        <label className="toggle">
          <input
            type="checkbox"
            checked={withPhoto}
            onChange={(change) => setWithPhoto(change.target.checked)}
          />
          <span className="toggle__track" aria-hidden="true" />
          <span className="toggle__text">
            <strong>{t("landing.studio.photo")}</strong>
            <small>{t("landing.studio.photoHint")}</small>
          </span>
        </label>
      </Reveal>

      <Reveal className="studio__stage" variant="zoom">
        <div className="studio__card" key={templateId}>
          <Suspense fallback={<div className="preview-loading" />}>
            <SitePreview event={event} template={template} heroOnly />
          </Suspense>
        </div>
      </Reveal>
    </div>
  );
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const { season, showEffects, showAmbient } = useSeason();
  const [openFaq, setOpenFaq] = useState(FAQ[0]);
  const { templatesForType, priceOf } = useCatalog();
  const offered = templatesForType("");
  const lowestPrice = formatPrice(
    Math.min(...(offered.length ? offered : TEMPLATES).map(priceOf)),
    i18n.language,
  );

  return (
    <div className="landing">
      <section className="hero">
        {showEffects && !showAmbient && (
          <SeasonalEffects effect={season.effect} />
        )}
        <span className="hero__blob hero__blob--1" />
        <span className="hero__blob hero__blob--2" />
        <span className="hero__blob hero__blob--3" />
        <div className="container hero__inner">
          <div className="hero__copy">
            <span className="pill pill--glow">✨ {t("brand.tagline")}</span>
            <h1>
              {t("landing.heroTitle")}{" "}
              <span className="gradient-text">
                {t("landing.heroHighlight")}
              </span>
            </h1>
            <p className="hero__lead">{t("landing.heroLead")}</p>
            <div className="hero__actions">
              <Link to="/create" className="btn btn--gradient btn--lg">
                {t("landing.ctaCreate")} →
              </Link>
              <Link to="/templates" className="btn btn--outline btn--lg">
                {t("landing.ctaBrowse")}
              </Link>
            </div>
            <ul className="hero__ticks">
              <li>{t("landing.tick1")}</li>
              <li>{t("landing.tick2")}</li>
              <li>{t("landing.tick3", { price: lowestPrice })}</li>
            </ul>
          </div>

          <div className="hero__cards" aria-hidden="true" inert="">
            <div className="hero__card hero__card--1">
              <InvitationCard
                event={sampleEvent(t, "birthday")}
                template={getTemplate("confetti-pop")}
                compact
              />
            </div>
            <div className="hero__card hero__card--3">
              <InvitationCard
                event={sampleEvent(t, "meeting")}
                template={getTemplate("boardroom")}
                compact
              />
            </div>
            <div className="hero__card hero__card--2">
              <InvitationCard
                event={{
                  ...sampleEvent(t, "wedding"),
                  coverUrl: samplePhoto,
                }}
                template={getTemplate("jardin")}
                compact
              />
            </div>
            <div className="hero__toast hero__toast--1">
              <span>🎉</span>
              <div>
                <strong>{t("landing.toast1.title")}</strong>
                <small>{t("landing.toast1.text")}</small>
              </div>
            </div>
            <div className="hero__toast hero__toast--2">
              <span>🍽️</span>
              <div>
                <strong>{t("landing.toast2.title")}</strong>
                <small>{t("landing.toast2.text")}</small>
              </div>
            </div>
          </div>
        </div>

        <div className="marquee" aria-hidden="true">
          <div className="marquee__track">
            {[0, 1].map((copy) =>
              EVENT_TYPES.map((type) => (
                <span key={`${copy}-${type.id}`}>
                  {type.emoji} {t(`types.${type.id}.name`)}
                </span>
              )),
            )}
          </div>
        </div>
      </section>

      <section className="section section--popular">
        <div className="container">
          <Reveal as="header" className="section__head">
            <h2>
              {t("landing.popular.title")} <span className="section__script">{t("landing.popular.highlight")}</span>
            </h2>
            <p>{t("landing.popular.lead")}</p>
          </Reveal>
          <Suspense fallback={<div className="preview-loading" />}>
            <TemplateCarousel />
          </Suspense>
          <p className="section__more">
            <Link to="/templates" className="btn btn--gradient btn--lg">
              {t("landing.popular.browse", { count: offered.length || TEMPLATES.length })} →
            </Link>
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal as="header" className="section__head">
            <span className="eyebrow">{t("landing.studio.eyebrow")}</span>
            <h2>{t("landing.studio.title")}</h2>
            <p>{t("landing.studio.lead")}</p>
          </Reveal>
          <Studio />
        </div>
      </section>

      <section className="section section--tint">
        <div className="container">
          <Reveal as="header" className="section__head">
            <span className="eyebrow">{t("landing.typesEyebrow")}</span>
            <h2>{t("landing.typesTitle")}</h2>
            <p>{t("landing.typesLead")}</p>
          </Reveal>
          <div className="types">
            {EVENT_TYPES.map((type, index) => (
              <Reveal key={type.id} delay={index * 60}>
                <Link
                  to={`/templates?type=${type.id}`}
                  className="type-card"
                  style={{ "--type": type.color }}
                >
                  <span className="type-card__emoji">{type.emoji}</span>
                  <strong>{t(`types.${type.id}.name`)}</strong>
                  <small>{t(`types.${type.id}.desc`)}</small>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container showcase">
          <div className="showcase__row">
            <Reveal className="showcase__copy" variant="left">
              <span className="eyebrow">{t("landing.story.eyebrow")}</span>
              <h2>{t("landing.story.title")}</h2>
              <p>{t("landing.story.text")}</p>
              <ul className="ticks">
                {POINTS.map((point) => (
                  <li key={point}>{t(`landing.story.${point}`)}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="showcase__visual" variant="right">
              <div className="devices" aria-hidden="true" inert="">
                <Suspense fallback={<div className="preview-loading" />}>
                  <SitePreview
                    event={demoEvent(t, "wedding", getTemplate("minuit-dore"))}
                    template={getTemplate("minuit-dore")}
                    height="400px"
                  />
                  <div className="phone phone--small">
                    <div className="phone__screen">
                      <SitePreview
                        event={demoEvent(t, "dinner", getTemplate("riviera"))}
                        template={getTemplate("riviera")}
                        bare
                        width={390}
                        height="100%"
                      />
                    </div>
                  </div>
                </Suspense>
              </div>
            </Reveal>
          </div>

          <div className="showcase__row showcase__row--flip">
            <Reveal className="showcase__copy" variant="right">
              <span className="eyebrow">{t("landing.rsvp.eyebrow")}</span>
              <h2>{t("landing.rsvp.title")}</h2>
              <p>{t("landing.rsvp.text")}</p>
              <ul className="ticks">
                {POINTS.map((point) => (
                  <li key={point}>{t(`landing.rsvp.${point}`)}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="showcase__visual" variant="left">
              <div className="mock card" aria-hidden="true" inert="">
                <strong className="mock__title">{t("rsvp.title")}</strong>
                <div className="answer">
                  <span className="answer__btn is-active">
                    <span>🎉</span>
                    {t("rsvp.answers.yes")}
                  </span>
                  <span className="answer__btn">
                    <span>🤔</span>
                    {t("rsvp.answers.maybe")}
                  </span>
                  <span className="answer__btn">
                    <span>😢</span>
                    {t("rsvp.answers.no")}
                  </span>
                </div>
                <span className="field__label">{t("rsvp.meal")}</span>
                {["meat", "fish", "veggie"].map((meal, index) => (
                  <span
                    key={meal}
                    className={`meal-option${index === 2 ? " is-active" : ""}`}
                  >
                    <i
                      className={`mock__radio${index === 2 ? " is-on" : ""}`}
                    />
                    <span>
                      <strong>{t(`editor.presetMeals.${meal}.name`)}</strong>
                      <small>
                        {t(`editor.presetMeals.${meal}.description`)}
                      </small>
                    </span>
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="showcase__row">
            <Reveal className="showcase__copy" variant="left">
              <span className="eyebrow">{t("landing.host.eyebrow")}</span>
              <h2>{t("landing.host.title")}</h2>
              <p>{t("landing.host.text")}</p>
              <ul className="ticks">
                {POINTS.map((point) => (
                  <li key={point}>{t(`landing.host.${point}`)}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="showcase__visual" variant="right">
              <div className="mock mock--dash card" aria-hidden="true">
                <div className="stats">
                  <div className="stat stat--accent">
                    <strong>128</strong>
                    <span>{t("manage.stats.headcount")}</span>
                  </div>
                  <div className="stat">
                    <strong>61</strong>
                    <span>{t("manage.stats.yes")}</span>
                  </div>
                </div>
                <ul className="meals__list">
                  {[
                    ["meat", 54],
                    ["fish", 31],
                    ["veggie", 15],
                  ].map(([meal, share]) => (
                    <li key={meal}>
                      <div className="meals__row">
                        <span>{t(`editor.presetMeals.${meal}.name`)}</span>
                        <strong>{Math.round((share * 128) / 100)}</strong>
                      </div>
                      <div className="meals__bar">
                        <span style={{ width: `${share}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section section--tint">
        <div className="container">
          <Reveal as="header" className="section__head">
            <span className="eyebrow">{t("landing.howEyebrow")}</span>
            <h2>{t("landing.howTitle")}</h2>
            <p>{t("landing.howLead")}</p>
          </Reveal>
          <ol className="steps">
            {STEPS.map((step, index) => (
              <Reveal
                as="li"
                key={step}
                className="steps__item"
                delay={index * 110}
              >
                <span className="steps__num">{index + 1}</span>
                <h3>{t(`landing.steps.${step}.title`)}</h3>
                <p>{t(`landing.steps.${step}.text`)}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="section">
        <div className="container pricing">
          <Reveal className="pricing__copy" variant="left">
            <span className="eyebrow">{t("landing.pricing.eyebrow")}</span>
            <h2>{t("landing.pricing.title")}</h2>
            <p>{t("landing.pricing.lead")}</p>
          </Reveal>
          <Reveal className="pricing__card" variant="zoom">
            <p className="pricing__from">{t("landing.pricing.from")}</p>
            <p className="pricing__price">
              {lowestPrice}
              <small>{t("landing.pricing.per")}</small>
            </p>
            <ul className="ticks ticks--light">
              {INCLUDED.map((item) => (
                <li key={item}>{t(`landing.pricing.${item}`)}</li>
              ))}
            </ul>
            <Link to="/create" className="btn btn--light btn--lg btn--block">
              {t("landing.ctaCreate")}
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="section section--tint">
        <div className="container container--narrow">
          <Reveal as="header" className="section__head">
            <h2>{t("landing.faq.title")}</h2>
          </Reveal>
          <div className="faq">
            {FAQ.map((id, index) => (
              <Reveal
                key={id}
                delay={index * 60}
                className={`faq__item${openFaq === id ? " is-open" : ""}`}
              >
                <button
                  type="button"
                  aria-expanded={openFaq === id}
                  onClick={() => setOpenFaq(openFaq === id ? "" : id)}
                >
                  {t(`landing.faq.${id}.q`)}
                  <span aria-hidden="true">+</span>
                </button>
                <div className="faq__answer">
                  <p>{t(`landing.faq.${id}.a`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container">
        <Reveal className="cta-band" variant="zoom">
          <h2>{t("landing.bandTitle")}</h2>
          <p>{t("landing.bandLead")}</p>
          <Link to="/create" className="btn btn--light btn--lg">
            {t("landing.ctaCreate")} →
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
